/**
 * Date Range Preset Selector Component
 * Dropdown for predefined date ranges that automatically updates the date range filter
 */

import { Select } from 'antd';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../../store/hooks';
import { setFilters } from '../../../store/dashboardSlice';

interface DateRangePresetSelectorProps {
  className?: string;
}

interface DateRangePreset {
  label: string;
  value: string;
  getDateRange: () => { start: Date; end: Date };
}

const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: 'This Month',
    value: 'this_month',
    getDateRange: () => {
      const now = dayjs();
      return {
        start: now.startOf('month').toDate(),
        end: now.endOf('day').toDate()
      };
    }
  },
  {
    label: '90 Days',
    value: '90_days',
    getDateRange: () => {
      const now = dayjs();
      return {
        start: now.subtract(90, 'days').startOf('day').toDate(),
        end: now.endOf('day').toDate()
      };
    }
  },
  {
    label: '180 Days',
    value: '180_days',
    getDateRange: () => {
      const now = dayjs();
      return {
        start: now.subtract(180, 'days').startOf('day').toDate(),
        end: now.endOf('day').toDate()
      };
    }
  },
  {
    label: 'This Year',
    value: 'this_year',
    getDateRange: () => {
      const now = dayjs();
      return {
        start: now.startOf('year').toDate(),
        end: now.endOf('day').toDate()
      };
    }
  },
  {
    label: 'One Year',
    value: 'one_year',
    getDateRange: () => {
      const now = dayjs();
      return {
        start: now.subtract(1, 'year').startOf('day').toDate(),
        end: now.endOf('day').toDate()
      };
    }
  }
];

function DateRangePresetSelector({ className }: DateRangePresetSelectorProps) {
  const dispatch = useAppDispatch();

  // Determine current preset value - default to "90 Days" as requested
  const getCurrentPreset = (): string => {
    // Always default to 90 days as requested by user
    return '90_days';
  };

  const handlePresetChange = (presetValue: string) => {
    const preset = DATE_RANGE_PRESETS.find(p => p.value === presetValue);
    if (preset) {
      const { start, end } = preset.getDateRange();
      dispatch(setFilters({
        dateRange: { start, end }
      }));
      
      // Auto-refresh will be handled by the useEffect in DashboardPage that watches for filter changes
    }
  };

  return (
    <div className={`filter ${className || ''}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <label htmlFor="date-range-preset">Quick Range:</label>
      <Select
        id="date-range-preset"
        value={getCurrentPreset()}
        onChange={handlePresetChange}
        placeholder="Select preset"
        style={{ width: '140px' }}
        options={DATE_RANGE_PRESETS.map(preset => ({
          label: preset.label,
          value: preset.value
        }))}
      />
    </div>
  );
}

export default DateRangePresetSelector;